import { useState } from 'react';
import { DayPicker, type DateRange, type Matcher } from 'react-day-picker';
import 'react-day-picker/style.css';
import { format, isSameDay, startOfDay, startOfMonth, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CalendarDays, ChevronDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover.js';
import { dateFnsLocale } from '@/lib/datetime/locale.js';
import { cn } from '@/lib/utils/cn.js';

export type { DateRange };

interface DateRangePickerProps {
  value?: DateRange;
  /** Called with a complete range (both ends set), or `undefined` for "All time" — never with half a range. */
  onChange: (range: DateRange | undefined) => void;
  /** What the range filters, e.g. "Date range" — the group's accessible name. */
  label: string;
  /** The day presets count back from and the last pickable day. Defaults to now; tests pin it. */
  today?: Date;
}

const PRESETS = ['all', 'today', 'last7', 'last30', 'thisMonth'] as const;
type Preset = (typeof PRESETS)[number];

function presetRange(preset: Exclude<Preset, 'all'>, today: Date): { from: Date; to: Date } {
  switch (preset) {
    case 'today':     return { from: today, to: today };
    case 'last7':     return { from: subDays(today, 6), to: today };
    case 'last30':    return { from: subDays(today, 29), to: today };
    case 'thisMonth': return { from: startOfMonth(today), to: today };
  }
}

function matchesPreset(preset: Preset, value: DateRange | undefined, today: Date): boolean {
  if (!value?.from || !value.to) return preset === 'all';
  if (preset === 'all') return false;
  const range = presetRange(preset, today);
  return isSameDay(range.from, value.from) && isSameDay(range.to, value.to);
}

/**
 * Preset chips plus two separately labelled "From" and "To" fields, each with
 * its own calendar, so a player always knows which end of the range they are
 * setting. Picking only From runs the range up to today; picking only To makes
 * it that single day.
 */
export function DateRangePicker({ value, onChange, label, today: todayProp }: DateRangePickerProps) {
  const { t } = useTranslation();
  const today = startOfDay(todayProp ?? new Date());
  // "This month" equals "Today" on the 1st, "7 days" on the 7th and "30 days" on the 30th, so the chip the player pressed wins over the first match.
  const [pressed, setPressed] = useState<Preset>();
  const active =
    pressed && matchesPreset(pressed, value, today)
      ? pressed
      : PRESETS.find((p) => matchesPreset(p, value, today));

  function pickPreset(preset: Preset) {
    setPressed(preset);
    onChange(preset === 'all' ? undefined : presetRange(preset, today));
  }

  function pickFrom(day: Date) {
    const to = value?.to ?? today;
    onChange({ from: day, to: day > to ? day : to });
  }

  function pickTo(day: Date) {
    const from = value?.from ?? day;
    onChange({ from: from > day ? day : from, to: day });
  }

  return (
    <div role="group" aria-label={label} className="flex w-full min-w-0 flex-col gap-2 sm:max-w-md">
      <div className="flex flex-wrap gap-1 rounded-lg bg-[var(--bg-raised)] p-1">
        {PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            aria-pressed={active === preset}
            onClick={() => pickPreset(preset)}
            className={cn(
              'h-8 flex-auto whitespace-nowrap rounded-md px-2.5 text-[12px] font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-primary)]',
              active === preset
                ? 'bg-[var(--primary)] text-[var(--primary-on)]'
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]',
            )}
          >
            {t(`datePicker.preset.${preset}`)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5">
        <DateField
          label={t('datePicker.from')}
          date={value?.from}
          today={today}
          disabled={{ after: value?.to ?? today }}
          onPick={pickFrom}
        />
        <ArrowRight size={14} aria-hidden className="text-[var(--text-muted)]" />
        <DateField
          label={t('datePicker.to')}
          date={value?.to}
          today={today}
          disabled={value?.from ? [{ after: today }, { before: value.from }] : { after: today }}
          onPick={pickTo}
        />
      </div>
    </div>
  );
}

interface DateFieldProps {
  label: string;
  date: Date | undefined;
  today: Date;
  disabled: Matcher | Matcher[];
  onPick: (day: Date) => void;
}

function DateField({ label, date, today, disabled, onPick }: DateFieldProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const locale = dateFnsLocale(i18n.language);
  const shown = date ? format(date, 'd MMM yyyy', { locale }) : t('datePicker.choose');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${shown}`}
          className={cn(
            'flex min-h-11 w-full min-w-0 items-center gap-2 rounded-lg border bg-[var(--bg-elevated)] px-2.5 py-1.5 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-primary)]',
            open ? 'border-[var(--primary)]' : 'border-[var(--border-strong)] hover:border-[var(--text-muted)]',
          )}
        >
          <CalendarDays size={16} aria-hidden className="shrink-0 text-[var(--text-muted)]" />
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              {label}
            </span>
            <span
              className={cn(
                'truncate text-[13px] font-semibold',
                date ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]',
              )}
            >
              {shown}
            </span>
          </span>
          <ChevronDown size={14} aria-hidden className="shrink-0 text-[var(--text-muted)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-label={label} className="w-auto max-w-[calc(100vw-2rem)] p-2">
        <p className="px-2 pt-1 pb-2 text-[12px] font-semibold text-[var(--text-secondary)]">{label}</p>
        <DayPicker
          mode="single"
          required
          locale={locale}
          selected={date}
          onSelect={(day) => {
            onPick(day);
            setOpen(false);
          }}
          disabled={disabled}
          today={today}
          defaultMonth={date ?? today}
          endMonth={today}
          captionLayout="dropdown"
          classNames={{ today: 'font-extrabold underline decoration-2 underline-offset-4' }}
          style={
            {
              '--rdp-accent-color': 'var(--text-primary)',
              '--rdp-accent-background-color': 'var(--primary-glow)',
              '--rdp-background-color': 'var(--bg-elevated)',
              '--rdp-day-font-size': '13px',
            } as React.CSSProperties
          }
        />
      </PopoverContent>
    </Popover>
  );
}
